"use client";

import { useState } from "react";
import { Check, ChevronRight, PartyPopper, RotateCcw, X } from "lucide-react";
import { api, type Exercise } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function ExerciseBlock({
  exercises,
  conceptTitle,
  onAnswered,
  onFinish,
}: {
  exercises: Exercise[];
  conceptTitle: string;
  onAnswered?: (correct: boolean) => void;
  onFinish?: () => void;
}) {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<{ correct: boolean; answer: string; correct_answer: string; explanation: string } | null>(null);
    const [finished, setFinished] = useState(false);
  const [score, setScore] = useState(0);

  if (!exercises || exercises.length === 0) return null;

  const ex = exercises[current];
  if (!ex || !ex.question || !Array.isArray(ex.options) || ex.options.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
        该练习题数据不完整，暂时无法作答。可先跳过进入主动回忆环节。
      </div>
    );
  }
  const answered = result !== null;
  const isLast = current === exercises.length - 1;

  // 本地确定性判分（与后端 grading.py 同一套规则）：
  // 提交的可能是完整选项文本（"B. 不可以"）或字母（"B"），答案存的是字母
  function gradeLocally(exercise: Exercise, submitted: string): boolean {
    const sub = (submitted || "").trim();
    if (!sub) return false;
    const correct = (exercise.answer || "").trim();
    const norm = (t: string) =>
      t.toLowerCase().replace(/[^a-z0-9\u4e00-\u9fff]/g, "").replace(/\s+/g, "");
    if (norm(sub) === norm(correct)) return true;
    if (exercise.type === "single_choice") {
      const subLetter = sub.charAt(0).toUpperCase();
      const correctLetter = correct.charAt(0).toUpperCase();
      if (subLetter === correctLetter) return true;
    }
    return false;
  }

  function submit(answer: string) {
    setSelected(answer);
    // 本地即时判分：题目/答案/解析都是题库预置，点击瞬间出结果，不等后端
    const correct = gradeLocally(ex, answer);
    setResult({
      correct,
      answer,
      correct_answer: ex.answer,
      explanation: ex.explanation,
    });
    if (correct) setScore((s) => s + 1);
    onAnswered?.(correct);
    // 后台异步上报答题记录（失败自动重试一次，不影响前端展示）
    api.attempt(ex.id, answer)
      .catch(() => new Promise((r) => setTimeout(r, 600)).then(() => api.attempt(ex.id, answer)))
      .catch(() => {});
  }

  function next() {
    if (isLast) {
      setFinished(true);
      return;
    }
    setSelected(null);
    setResult(null);
    setCurrent((c) => c + 1);
  }

  function restart() {
    setCurrent(0);
    setSelected(null);
    setResult(null);
    setScore(0);
    setFinished(false);
  }

  if (finished) {
    const rate = Math.round((score / exercises.length) * 100);
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-8 text-center animate-rise">
          <div
            className={cn(
              "flex h-14 w-14 items-center justify-center rounded-full",
              rate === 100 ? "bg-success/10 text-success" : rate >= 60 ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"
            )}
          >
            <PartyPopper className="h-7 w-7" />
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums">
              {score} / {exercises.length}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {rate === 100
                ? "全对！这个知识点已经稳了。"
                : rate >= 60
                  ? "不错，错题可以回顾一下解析。"
                  : "建议回看讲解再来一轮。"}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="outline" onClick={restart}>
              <RotateCcw className="h-4 w-4" />
              再练一轮
            </Button>
            {onFinish && (
              <Button onClick={onFinish}>
                进入主动回忆
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-5">
        {/* 题目标题 + 进度 */}
        <div className="mb-4 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            第 {current + 1} / {exercises.length} 题
          </span>
          <span className="text-xs text-muted-foreground">{conceptTitle}</span>
        </div>
        <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${((current + (answered ? 1 : 0)) / exercises.length) * 100}%` }}
          />
        </div>

        <h3 key={ex.id} className="mb-4 text-base font-semibold animate-rise">{ex.question}</h3>

        <div className="space-y-2">
          {ex.options.map((opt) => {
            const letter = opt.charAt(0);
            const isCorrectOpt = answered && opt.startsWith(result.correct_answer);
            const isWrongPick = answered && selected === opt && !result.correct;
            return (
              <button
                key={opt}
                onClick={() => !answered && submit(opt)}
                disabled={answered}
                className={cn(
                  "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-all",
                  !answered &&
                    "cursor-pointer hover:border-primary/50 hover:bg-primary/5 active:scale-[0.99]",
                  isCorrectOpt && "border-success bg-success/10 animate-pop",
                  isWrongPick && "border-destructive bg-destructive/10 animate-shake",
                  answered && !isCorrectOpt && !isWrongPick && "opacity-60"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                    isCorrectOpt && "border-success text-success",
                    isWrongPick && "border-destructive text-destructive",
                    !answered && selected === opt && "border-primary text-primary",
                    !answered && selected !== opt && "border-border text-muted-foreground"
                  )}
                >
                  {isCorrectOpt ? <Check className="h-3 w-3" /> : isWrongPick ? <X className="h-3 w-3" /> : letter}
                </span>
                <span className="pt-px">{opt}</span>
              </button>
            );
          })}
        </div>

        {/* 结果反馈 */}
        {result && (
          <div
            className={cn(
              "mt-4 rounded-lg border p-4 text-sm animate-rise",
              result.correct ? "border-success/40 bg-success/10" : "border-destructive/40 bg-destructive/10"
            )}
          >
            <div className="mb-1 flex items-center gap-1.5 font-semibold">
              {result.correct ? (
                <>
                  <Check className="h-4 w-4 text-success" />
                  <span className="text-success">回答正确</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4 text-destructive" />
                  <span className="text-destructive">
                    回答错误，正确答案 {result.correct_answer}
                  </span>
                </>
              )}
            </div>
            <p className="text-muted-foreground">{result.explanation}</p>
          </div>
        )}

        {answered && (
          <div className="sticky bottom-[calc(var(--mobile-tab-height)+env(safe-area-inset-bottom)+0.75rem)] z-10 -mx-5 -mb-5 mt-4 bg-gradient-to-t from-card via-card/90 to-transparent px-5 pb-2 pt-6 sm:bottom-3">
            <div className="flex justify-end">
              <Button onClick={next}>
                {isLast ? "完成练习" : "下一题"}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
